import { memo } from 'react';

import { type Board, type Clip } from '@air/api/types';

import { type UseGalleryAssetsParams } from '~/components/Gallery/useGalleryAssets';
import { type UseGalleryBoardParams } from '~/components/Gallery/useGalleryBoards';
import { type UseGalleryFilesParams } from '~/components/Gallery/useGalleryFiles';
import { PrivateBoardGalleryViewAsset } from '~/components/v2/components/board/gallery/PrivateBoardGalleryViewAsset';
import { PrivateBoardGalleryViewBoard } from '~/components/v2/components/board/gallery/PrivateBoardGalleryViewBoard';
import { PrivateBoardGalleryViewFile } from '~/components/v2/components/board/gallery/PrivateBoardGalleryViewFile';
import {
  PrivateGalleryView,
  type PrivateGalleryViewProps,
} from '~/components/v2/components/shared/gallery/PrivateGalleryView';
import { usePrivateWorkspaceAgentAssetListContext } from '~/components/v2/components/workspace/workspace-agent-asset-list/PrivateWorkspaceAgentAssetListProvider';
import { WorkspaceAgentAssetListNullState } from '~/components/v2/components/workspace/workspace-agent-asset-list/WorkspaceAgentAssetListNullState';

export type PrivateWorkspaceAgentAssetListGalleryViewProps = Pick<PrivateGalleryViewProps, 'scrollElementRef'>;

/** Never called: an asset list artifact holds no boards. The gallery still requires a renderer. */
const renderBoard: NonNullable<UseGalleryBoardParams<Board>['renderBoard']> = ({ box, data, index }) => (
  <PrivateBoardGalleryViewBoard box={box} data={data} index={index} key={data.id} />
);

const renderAsset: NonNullable<UseGalleryAssetsParams<Clip>['renderAsset']> = ({ box, data, index }) => (
  <PrivateBoardGalleryViewAsset box={box} data={data} index={index} key={data.id} />
);

const renderFile: NonNullable<UseGalleryFilesParams<Clip>['renderFile']> = ({ box, data, index }) => (
  <PrivateBoardGalleryViewFile box={box} data={data} index={index} key={data.id} />
);

const useNoVisibleCustomFields = () => ({ permissionedVisibleCustomFieldsLength: 0 });

export const PrivateWorkspaceAgentAssetListGalleryView = memo(
  ({ scrollElementRef }: PrivateWorkspaceAgentAssetListGalleryViewProps) => {
    const { galleryItemsData } = usePrivateWorkspaceAgentAssetListContext();

    return (
      <PrivateGalleryView
        NullState={WorkspaceAgentAssetListNullState}
        cardSize="medium"
        itemsData={galleryItemsData}
        renderAsset={renderAsset}
        renderBoard={renderBoard}
        renderFile={renderFile}
        scrollElementRef={scrollElementRef}
        showTitleAndMetadata
        useCustomFieldsLength={useNoVisibleCustomFields}
      />
    );
  },
);

PrivateWorkspaceAgentAssetListGalleryView.displayName = 'PrivateWorkspaceAgentAssetListGalleryView';
